import PropTypes from "prop-types";
import React, { useEffect, useState, useContext } from "react";
import { FormFieldsContext } from "../../../contexts/form-fields.context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";

import {
  createProperty,
  getSingleProperty,
  updateProperty,
} from "../../../api/wordpress";

import InputField from "../../fields/input-field/form-input.component";
import SelectField from "../../fields/select-field/form-select.component";
import BtnLoader from "../../loaders/btn-loader.component";
import PageLoader from "../../loaders/page-loader.component";
import ErrorTextPane from "../../text-boxes/info-panes/error-status-pane.component";
import SuccessModal from "../../modals/success-modal.component";
import CurrencyInputField from "../../fields/input-field/currency-input.component";
import axios from "axios";

const defaultErrorObject = {
  code: "",
  status: false,
  message: "",
};

const defaultFormFields = {
  id: false,
  price: 0,
  post_title: "",
  content: "",
  other_amenity_field: "",
  address: "",
  property_type: "",
  amenities: [],
  preferred_gender: "",
  gallery: [],
  state: "",
  city: "",
  street: "",
  lga: "",
  landmark: "",
  occupancy_status: false,
};

const RECEIPT_REQUIRED = true;

const PublishPropertyForm = ({ setReloadPosts, setParams }) => {
  const [params] = useSearchParams();
  const [addressObj] = useState();

  const { amenitiesOptions, genders, propertyTypes } = useContext(FormFieldsContext);
  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const user_email = JSON.parse(localStorage.getItem("user"))?.user?.user_email;
  const user_nickname = JSON.parse(localStorage.getItem("user"))?.user?.nickname;
  const user_firstname = JSON.parse(localStorage.getItem("user"))?.user?.first_name;
  const user_lastname = JSON.parse(localStorage.getItem("user"))?.user?.last_name;
  const phone_number = JSON.parse(localStorage.getItem("user"))?.user?.phone_number;

  const navigate = useNavigate();

  const [loader, setLoader] = useState(true);
  const [enableOtherField, setEnableOtherField] = useState(false);
  const [btnloader, setBtnLoading] = useState(false);
  const [modalState, setModalState] = useState(false);

  const [formFields, setFormFields] = useState(defaultFormFields);
  const [files, setFile] = useState([]);
  const [imageMessage, setImageMessage] = useState("");

  const [videoFile, setVideoFile] = useState(null);
  const [videoMessage, setVideoMessage] = useState("");
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState(null);

  const [existingGallery, setExistingGallery] = useState([]);
  const [existingVideo, setExistingVideo] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptMessage, setReceiptMessage] = useState("");
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [uploadedReceiptUrl, setUploadedReceiptUrl] = useState(null);
  const [existingReceipt, setExistingReceipt] = useState(null);

  const {
    id,
    post_title,
    other_amenity_field,
    price,
    content,
    property_type,
    amenities,
    preferred_gender,
    city,
    state,
    lga,
    landmark,
    street,
    occupancy_status,
  } = formFields;

  const [errorObj, setLoginError] = useState(defaultErrorObject);
  const [stateOptions, setStateOptions] = useState([]);
  const [lgaOptions, setLgaOptions] = useState([]);

  const isFormDisabled = btnloader;

  const getToken = async () => {
    const response = await axios.post("https://api.qoreid.com/token", {
      clientId: "RJQESSU6GNNHO7ZT0QWD",
      secret: "65aa73f854a342b5b21c08375e174bfa",
    });
    return response?.data.accessToken;
  };

  const fetchStates = async () => {
    const token = await getToken();
    const statesResponse = await axios.get(
        "https://api.qoreid.com/v1/countries/1/states",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
    );
    const mappedStates = statesResponse?.data.map((state) => {
      return { value: `${state.name}-${state.id}`, name: state.name };
    });
    setStateOptions(mappedStates);
    return mappedStates;
  };

  const fetchLgas = async (stateId) => {
    const token = await getToken();
    const lgasResponse = await axios.get(
        `https://api.qoreid.com/v1/countries/1/states/${stateId}/lgas`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
    );
    setLgaOptions(
        lgasResponse.data.map((lga) => {
          return { value: lga.name.trim(), name: lga.name };
        })
    );
  };

  const handleReceiptFile = async (e) => {
    setReceiptMessage("");
    const file = e.target.files[0];

    if (!file) return;

    const validReceiptTypes = [
      "application/pdf",
      "image/gif",
      "image/jpeg",
      "image/png",
      "image/heic",
      "image/heif",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!validReceiptTypes.includes(file.type)) {
      setReceiptMessage("Please upload a valid receipt file (PDF, image, or document).");
      e.target.value = null;
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setReceiptMessage("Receipt is too large. Maximum size is 10MB.");
      e.target.value = null;
      return;
    }

    setReceiptMessage("Receipt ready to be attached to your property submission.");
    setReceiptFile(file);
    setUploadedReceiptUrl(null);
    setIsUploadingReceipt(false);

    e.target.value = null;
  };

  const handleVideoFile = (e) => {
    setVideoMessage("");
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("video/")) {
      setVideoMessage("Please upload a valid video format.");
      e.target.value = null;
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setVideoMessage("Video is too large. Maximum size is 50MB.");
      e.target.value = null;
      return;
    }

    const videoElement = document.createElement("video");
    videoElement.preload = "metadata";
    videoElement.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(videoElement.src);

      if (videoElement.duration < 10) {
        setVideoMessage("Video must be a minimum of 1 minute (60 seconds) long.");
        setVideoFile(null);
      } else {
        setVideoMessage("");
        setVideoFile(file);

        setIsUploadingVideo(true);
        setVideoMessage("Uploading your video...");

        try {
          const cloudinaryData = new FormData();
          cloudinaryData.append("file", file);
          cloudinaryData.append("upload_preset", "Ruumies Upload");
          cloudinaryData.append("resource_type", "video");

          const cloudResponse = await axios.post(
              `https://api.cloudinary.com/v1_1/dirodfnej/video/upload`,
              cloudinaryData
          );

          setUploadedVideoUrl(cloudResponse.data.secure_url);
          setVideoMessage("Video successfully uploaded!");
        } catch (error) {
          console.error("Cloudinary upload failed:", error);
          setVideoMessage("Failed to upload video to the cloud. Please remove and try again.");
          setVideoFile(null);
          setUploadedVideoUrl(null);
        } finally {
          setIsUploadingVideo(false);
        }
      }
    };
    videoElement.src = URL.createObjectURL(file);
    e.target.value = null;
  };

  const handleFile = async (e) => {
    setImageMessage("");
    let fileList = Array.from(e.target.files);

    if (existingGallery.length + files.length + fileList.length > 5) {
      setImageMessage("You can only upload a maximum of 5 images.");
      e.target.value = null;
      return;
    }

    setImageMessage("Compressing images, please wait...");
    let newFilesArray = [];
    let hasSizeError = false;
    let hasTypeError = false;

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: "image/jpeg"
    };

    for (let i = 0; i < fileList.length; i++) {
      const fileType = fileList[i]["type"];
      const fileSize = fileList[i]["size"];
      const validImageTypes = ["image/gif", "image/jpeg", "image/png", "image/heic", "image/heif"];

      if (!validImageTypes.includes(fileType)) {
        hasTypeError = true;
        continue;
      }

      if (fileSize > 10 * 1024 * 1024) {
        hasSizeError = true;
        continue;
      }

      try {
        const compressedFile = await imageCompression(fileList[i], options);
        const safeFileName = fileList[i].name.replace(/\.[^/.]+$/, "") + ".jpg";
        const renamedFile = new File([compressedFile], safeFileName, {
          type: "image/jpeg",
        });

        newFilesArray.push(renamedFile);
      } catch (error) {
        console.error("Compression error:", error);
      }
    }

    setFile([...files, ...newFilesArray]);

    if (hasSizeError) {
      setImageMessage("One or more images were skipped because they exceed the 10MB limit.");
    } else if (hasTypeError) {
      setImageMessage("One or more files were skipped because they are not valid images.");
    } else {
      setImageMessage("");
    }

    e.target.value = null;
  };

  const removeImage = (i) => {
    setFile(files.filter((x) => x.name !== i));
  };

  const removeExistingImage = (indexToRemove) => {
    setExistingGallery(existingGallery.filter((_, index) => index !== indexToRemove));
  };

  const setDefaultFormFields = (data, propertyTypes, amenities, loadedStates) => {
    let tempFormFields = {};
    let extractedAddress = {};

    Object.keys(defaultFormFields).forEach((key) => {
      if (data[key] !== undefined) {
        if (key === "property_type") {
          tempFormFields[key] = propertyTypes?.find(
              (item) => item.name === data[key]
          )?.value || data[key];
        } else if (key === "id") {
          tempFormFields[key] = data[key];
        } else if (typeof data[key] === "string") {
          tempFormFields[key] = data[key].replace(/<\/?[^>]+(>|$)/g, "");
        } else {
          tempFormFields[key] = data[key];
        }
      } else if (key === "post_title") {
        tempFormFields[key] = data.title;
      } else if (data["acf"] && data["acf"][key] !== undefined) {
        if (key === "amenities") {
          tempFormFields[key] = [];
          if (Array.isArray(data["acf"][key])) {
            data["acf"][key].forEach((item) => {
              tempFormFields[key].push(
                  amenities?.find((obj) => obj.value === item["value"])?.value || item["value"]
              );
            });
            const otherAmenityIsChecked = data["acf"][key].find(
                (obj) => obj.value === "other"
            );
            if (otherAmenityIsChecked) {
              setEnableOtherField(true);
            }
          }
        } else if (key === "gallery") {
          tempFormFields[key] = [];
          let galleryUrls = [];
          if (Array.isArray(data["acf"][key])) {
            data["acf"][key].forEach((item) => {
              const url = typeof item === "string" ? item : item["full_image_url"] || item["url"];
              if (url) {
                tempFormFields[key].push(url);
                galleryUrls.push(url);
              }
            });
          }
          setExistingGallery(galleryUrls);
        } else {
          tempFormFields[key] = data["acf"][key];
        }
      }
      else if (extractedAddress[key]) {
        tempFormFields[key] = extractedAddress[key];
      }
      else {
        tempFormFields[key] = defaultFormFields[key];
      }
    });

    if (data?.acf?.address) {
      const parts = data.acf.address.split(',').map(s => s.trim());

      if (parts.length >= 4) {
        tempFormFields.state = parts[parts.length - 1].replace('.', '');
        tempFormFields.lga = parts[parts.length - 2];
        tempFormFields.city = parts[parts.length - 3];
        tempFormFields.street = parts.slice(0, parts.length - 3).join(', ');
      }
    }

    if (data?.acf?.property_video_url) {
      setExistingVideo(data.acf.property_video_url);
    }

    if (data?.acf?.property_receipt_url) {
      setExistingReceipt(data.acf.property_receipt_url);
    }

    if (tempFormFields.state && loadedStates) {
      const matchedState = loadedStates.find(s => s.name.toLowerCase() === tempFormFields.state.toLowerCase() || s.value.split('-')[0].toLowerCase() === tempFormFields.state.toLowerCase());
      console.log("Matched state:", matchedState);

      if (matchedState) {
        tempFormFields.state = matchedState.name;
        const stateId = matchedState.value.split("-")[1];
        fetchLgas(stateId);
      }
    }

    console.log("Initialized form fields:", tempFormFields);

    setFormFields(tempFormFields);
  };

  const handleChange = (event) => {
    let { name, value } = event.target;
    if (name === "amenities[]") {
      if (value === "other") {
        setEnableOtherField(!enableOtherField);
      }
      if (formFields["amenities"].includes(value)) {
        const clonedFormFields = { ...formFields };
        clonedFormFields["amenities"].splice(
            clonedFormFields["amenities"].findIndex((amenity) => amenity === value),
            1
        );
        setFormFields(clonedFormFields);
      } else {
        setFormFields({
          ...formFields,
          amenities: [...formFields["amenities"], value],
        });
      }
    } else {
      if (name === "state") {
        setLgaOptions([]);
        const splittedValue = value.split("-");
        value = splittedValue[0].trim();
        fetchLgas(splittedValue[1]);
      }
      setFormFields({ ...formFields, [name]: value });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (price < 10000) {
      setLoginError({ code: 500, message: "Price must be in the range of N10,000 above.", status: true });
      toast.error("Price must be in the range of N10,000 above.");
      return;
    }

    if (!uploadedVideoUrl && !existingVideo) {
      setLoginError({ code: 500, message: "A property tour video (minimum 1 minute) is required to be fully uploaded.", status: true });
      toast.error("A property tour video (minimum 1 minute) is required to be fully uploaded.");
      return;
    }

    if (files.length === 0 && existingGallery.length === 0) {
      setLoginError({ code: 500, message: "Please upload at least one image.", status: true });
      toast.error("Please upload at least one image.");
      return;
    }

    if (RECEIPT_REQUIRED && !uploadedReceiptUrl && !existingReceipt && !receiptFile) {
      setLoginError({ code: 500, message: "Please upload a receipt for verification.", status: true });
      toast.error("Please upload a receipt for verification.");
      return;
    }

    setBtnLoading(true);

    const token = await getToken();
    const wptoken = JSON.parse(localStorage.getItem("user"))?.token;

    try {
      await axios.post(
          "https://api.qoreid.com/v1/addresses",
          {
            customerReference: user_nickname,
            street: street,
            lgaName: lga,
            stateName: state,
            landmark: landmark,
            city: city,
            applicant: {
              firstname: user_firstname,
              lastname: user_lastname,
              phone: phone_number,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.warn("Address validation failed, continuing upload...", err);
    }

    try {
      const formData = new FormData();
      formData.append("user_email", user_email);
      formData.set("price", price);
      formData.set("title", post_title);
      formData.set("post_title", post_title);
      formData.set("property_type", property_type);
      formData.set("city", city);
      formData.set("street", street);
      formData.set("landmark", landmark);
      formData.set("preferred_gender", preferred_gender);

      const stateName = state.includes("-") ? state.split("-")[0] : state;
      formData.set("state", state);
      formData.set("lga", lga);
      const generatedAddress = `${street}, ${city}, ${lga}, ${stateName}`;
      formData.set("address", generatedAddress);

      formData.set("content", content);

      formFields.amenities.forEach(amenity => {
        formData.append("amenities[]", amenity);
      });

      if (enableOtherField) {
        formData.set("other_amenity_field", other_amenity_field);
      }

      if (uploadedVideoUrl) {
        formData.append("property_video_url", uploadedVideoUrl);
      } else if (existingVideo) {
        formData.append("property_video_url", existingVideo);
      }

      if (receiptFile) {
        formData.append("receipt_file", receiptFile);
      }

      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          formData.append("files[]", files[i]);
        }
      }

      if (existingGallery && existingGallery.length > 0) {
        for (let i = 0; i < existingGallery.length; i++) {
          formData.append("existing_gallery[]", existingGallery[i]);
        }
      }

      formData.append("file_key", "gallery");
      formData.append("field_key", "field_626f6d4b0f2a8");

      if (params.get("action") === "edit") {
        formData.set("occupancy_status", occupancy_status ? 1 : 0);
        formData.append("id", id);
        await updateProperty(wptoken, formData);
      } else {
        formData.set("occupancy_status", 0);
        await createProperty(wptoken, formData);
      }

      setBtnLoading(false);
      setModalState(true);
    } catch (error) {
      setBtnLoading(false);
      const { code, message } = error.response?.data ? error.response.data : error;

      setLoginError({ code: code, message: message || "Upload failed", status: true });
      toast.error(message || "Upload failed");
    }
  };

  useEffect(() => {
    const initializeForm = async () => {
      try {
        const loadedStates = await fetchStates();

        if (params.get("action") === "edit") {
          const targetId = parseInt(params.get("id"));
          const response = await getSingleProperty(token, {
            include: targetId,
          });

          let propertyData = null;
          if (Array.isArray(response.data)) {
            propertyData = response.data.find(item => item.id === targetId);
            if (!propertyData && response.data.length > 0) {
              propertyData = response.data[0];
            }
          } else {
            propertyData = response.data;
          }

          if (propertyData) {
            setDefaultFormFields(propertyData, propertyTypes, amenitiesOptions, loadedStates);
          } else {
            toast.error("Could not find property details.");
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Error loading property.");
      } finally {
        setLoader(false);
      }
    };

    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    var components = {};
    if (addressObj) {
      var address_components = addressObj.address_components;
      address_components.forEach((address_component) => {
        address_component["types"].forEach((type) => {
          components[type] = address_component.long_name;
        });
      });
    }
  }, [addressObj]);

  return (
      <>
        {loader ? (
            <PageLoader size="150px" className="p-10 w-full flex justify-center" />
        ) : (
            <div className="md:w-[400px]" style={{ margin: "auto" }}>
              <form id="create-post-request_form" onSubmit={handleSubmit}>
                <fieldset disabled={isFormDisabled} className="disabled:opacity-60">
                  <ErrorTextPane status={errorObj.status} message={errorObj.message} />
                  <div className="bg-blue-50 p-2 mb-6 rounded-r-md">
                    <h3 className="text-primary font-semibold mb-1 text-sm">New Upload Requirement</h3>
                    <p className="text-sm text-gray-700">
                      To ensure quality listings for Ruumies, a property tour video is <span className="font-bold">mandatory</span>.
                      Your video must be a minimum of 1 minute long. Photos are optional but recommended.
                    </p>
                  </div>
                  <p className="block text-md font-normal mb-6 ">
                    {params.get("action") !== "edit"
                        ? "Fill in the details below to upload a property"
                        : "Edit the details of your property"}
                  </p>

                  <InputField label="Name of property" type="text" name="post_title" value={post_title} required onChange={handleChange} disabled={isFormDisabled} />
                  <SelectField options={propertyTypes} onChange={handleChange} label={"Type of Property"} name="property_type" required defaultValue={property_type} disabled={isFormDisabled} />
                  <SelectField options={stateOptions} onChange={handleChange} label={"State"} name="state" required defaultValue={stateOptions.find((option) => option.name === state)?.value} disabled={isFormDisabled} />

                  {!lgaOptions.length ? <></> : (
                      <SelectField options={lgaOptions} onChange={handleChange} label={"LGA"} name="lga" required defaultValue={lga} id="lgaOption" disabled={isFormDisabled} />
                  )}

                  <InputField label="City" type="text" name="city" value={city} required onChange={handleChange} disabled={isFormDisabled} />
                  <InputField label="Street" type="text" name="street" value={street} required onChange={handleChange} disabled={isFormDisabled} />
                  <InputField label="Landmark" type="text" name="landmark" value={landmark} required onChange={handleChange} disabled={isFormDisabled} />

                  <CurrencyInputField label="Cost Of Rent (per year)" name="price" value={price} required onChange={(v) => setFormFields({ ...formFields, price: v })} disabled={isFormDisabled} />

                  <SelectField options={genders} type="radio" onChange={handleChange} label={"Preferred Gender"} name="preferred_gender" required defaultValue={preferred_gender} disabled={isFormDisabled} />

                  <InputField label="Add a brief description of the property" type="textarea" name="content" value={content} required onChange={handleChange} placeholder="Feel free to talk about your occupation, likes, dislike and ideal property type." disabled={isFormDisabled} />

                  <SelectField options={amenitiesOptions} type="checkbox" onChange={handleChange} label={"Tick the amenities available"} name="amenities" required defaultValue={amenities} disabled={isFormDisabled} />

                  {enableOtherField && (
                      <InputField label="Other Amenities" type="text" name="other_amenity_field" value={other_amenity_field} required onChange={handleChange} disabled={isFormDisabled} />
                  )}

                  <div className="w-full relative flex flex-col mb-6 mt-4">
                    <p className="block text-sm text-gray-600 pb-1 text-left font-medium">Property Tour Video <span className="text-red-500">*</span></p>
                    <p className="text-xs text-gray-500 mb-2">Required: Min 1 minute length, Max 50MB file size.</p>

                    {isUploadingVideo ? (
                        <div className="relative border border-blue-200 p-4 rounded-md bg-blue-50 flex flex-col items-center justify-center space-y-2">
                          <svg className="animate-spin h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm font-medium text-blue-700 text-center">Uploading...<br/>You can continue filling out the form.</span>
                        </div>
                    ) : videoFile ? (
                        <div className="relative border p-2 rounded-md bg-gray-50 flex items-center justify-between">
                          <span className="text-sm truncate w-3/4 text-green-700">{videoFile.name} (Uploaded)</span>
                          <button
                              type="button"
                              onClick={() => {
                                setVideoFile(null);
                                setUploadedVideoUrl(null);
                                setVideoMessage("");
                              }}
                              disabled={isFormDisabled}
                              className="text-red-500 text-sm font-bold disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                    ) : existingVideo ? (
                        <div className="relative border p-2 rounded-md bg-gray-50 flex items-center justify-between">
                          <span className="text-sm truncate w-3/4 text-blue-600">Current Video Tour Attached</span>
                          <button
                              type="button"
                              onClick={() => setExistingVideo(null)}
                              disabled={isFormDisabled}
                              className="text-red-500 text-sm font-bold disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                    ) : (
                        <div className={`h-20 w-full relative border-2 items-center rounded-md bg-gray-100 border-gray-400 border-dotted flex justify-center ${isFormDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                          <input type="file" onChange={handleVideoFile} accept="video/*" disabled={isFormDisabled} className="h-full w-full opacity-0 z-10 absolute cursor-pointer disabled:cursor-not-allowed" />
                          <span className="text-sm text-gray-500">Tap to select video tour</span>
                        </div>
                    )}
                    {videoMessage && <span className={`text-[12px] mt-1 ${videoMessage.includes("successfully") ? "text-green-600" : "text-red-500"}`}>{videoMessage}</span>}
                  </div>

                  <div className="w-full relative flex flex-col mb-6 mt-2">
                    <p className="block text-sm text-gray-600 pb-1 text-left font-medium">Proof of payment / receipt {RECEIPT_REQUIRED ? <span className="text-red-500">*</span> : null}</p>
                    <p className="text-xs text-gray-500 mb-2">{RECEIPT_REQUIRED ? "Required for listing verification." : "Optional but recommended for listing verification."} Accepted formats: PDF, JPG, PNG, DOC.</p>

                    {isUploadingReceipt ? (
                      <div className="relative border border-blue-200 p-4 rounded-md bg-blue-50 flex flex-col items-center justify-center space-y-2">
                        <svg className="animate-spin h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm font-medium text-blue-700 text-center">Uploading receipt...</span>
                      </div>
                    ) : receiptFile ? (
                      <div className="relative border p-2 rounded-md bg-gray-50 flex items-center justify-between">
                        <span className="text-sm truncate w-3/4 text-green-700">{receiptFile.name} (Uploaded)</span>
                        <button
                          type="button"
                          onClick={() => {
                            setReceiptFile(null);
                            setUploadedReceiptUrl(null);
                            setReceiptMessage("");
                          }}
                          disabled={isFormDisabled}
                          className="text-red-500 text-sm font-bold disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    ) : existingReceipt ? (
                      <div className="relative border p-2 rounded-md bg-gray-50 flex items-center justify-between">
                        <span className="text-sm truncate w-3/4 text-blue-600">Current receipt attached</span>
                        <button
                          type="button"
                          onClick={() => setExistingReceipt(null)}
                          disabled={isFormDisabled}
                          className="text-red-500 text-sm font-bold disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className={`h-20 w-full relative border-2 items-center rounded-md bg-gray-100 border-gray-400 border-dotted flex justify-center ${isFormDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                        <input
                          data-testid="property-receipt-input"
                          type="file"
                          onChange={handleReceiptFile}
                          disabled={isFormDisabled}
                          className="h-full w-full opacity-0 z-10 absolute cursor-pointer disabled:cursor-not-allowed"
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.heic,.heif,.doc,.docx"
                        />
                        <span className="text-sm text-gray-500">Tap to upload receipt</span>
                      </div>
                    )}
                    {receiptMessage && <span className={`text-[12px] mt-1 ${receiptMessage.includes("successfully") ? "text-green-600" : "text-red-500"}`}>{receiptMessage}</span>}
                  </div>

                  <div className=" w-full relative flex flex-col mb-10">
                    <p className="block text-sm text-gray-600 pb-2 text-left font-medium">Add Images of property</p>
                    <p className="text-xs text-gray-500 mb-2">Max 5 images allowed. Max 10MB per image.</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {existingGallery.map((url, index) => (
                          <div key={`existing-${index}`} className="overflow-hidden relative">
                    <span
                        role="button"
                        tabIndex={isFormDisabled ? -1 : 0}
                        onClick={() => !isFormDisabled && removeExistingImage(index)}
                        onKeyDown={(e) => {
                          if (!isFormDisabled && (e.key === "Enter" || e.key === " ")) {
                            removeExistingImage(index);
                          }
                        }}
                        className={`absolute right-1 text-sm font-bold bg-black/30 rounded-full w-5 h-5 flex items-center justify-center text-white ${isFormDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:text-white"}`}
                    >
                      X
                    </span>
                            <img className="h-20 w-20 rounded-md object-cover" alt={`existing-img-${index}`} src={url} />
                          </div>
                      ))}

                      {files.map((file, key) => {
                        return (
                            <div key={key} className="overflow-hidden relative">
                      <span
                          role="button"
                          tabIndex={isFormDisabled ? -1 : 0}
                          onClick={() => !isFormDisabled && removeImage(file.name)}
                          onKeyDown={(e) => {
                            if (!isFormDisabled && (e.key === "Enter" || e.key === " ")) {
                              removeImage(file.name);
                            }
                          }}
                          className={`absolute right-1 text-sm font-bold bg-black/30 rounded-full w-5 h-5 flex items-center justify-center text-white ${isFormDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:text-white"}`}
                      >
                        X
                      </span>
                              <img className="h-20 w-20 rounded-md object-cover" alt={`img-upload-preview-${key}`} src={URL.createObjectURL(file)} />
                            </div>
                        );
                      })}
                    </div>
                    <span className="flex justify-center items-center text-[12px] mb-1 text-red-500">{imageMessage}</span>

                    <div className={`h-20 w-full relative border-2 items-center rounded-md bg-gray-100 border-gray-400 border-dotted flex justify-center mt-2 ${isFormDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                      <input type="file" onChange={handleFile} disabled={isFormDisabled} className="h-full w-full opacity-0 z-10 absolute cursor-pointer disabled:cursor-not-allowed" multiple="multiple" name="files[]" accept="image/*" />
                      <span className="text-sm text-gray-500">Tap to upload additional images</span>
                    </div>

                  </div>

                  <div className="field_wrapper mb-5 mt-8">
                    <button
                        type="submit"
                        name="submit"
                        id="create_property"
                        disabled={
                            isFormDisabled || isUploadingVideo || !post_title || !price || !content || !property_type || !street || !city || !state
                        }
                        className="w-full rounded-md text-white p-4 bg-primary hover:bg-blue-900 font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isFormDisabled ? (
                          <BtnLoader message={'Creating property...'} />
                      ) : (params.get("action") === "edit" ? "Submit for Review" : "Upload Property")}
                    </button>
                  </div>
                </fieldset>
              </form>
              <SuccessModal
                  action={() => {
                    setModalState(false);
                    if (params.get("action") === "edit") {
                      params.delete("action");
                      params.delete("id");
                      setParams(params);
                      setReloadPosts(true);
                    }
                    if (params.get("action") === "add-new") {
                      navigate("/dashboard/my-properties");
                    }
                  }}
                  state={modalState}
                  title="Property Submitted"
                  description="Your property has been submitted for review. It will be visible to Ruumies once approved by our team."
              />
            </div>
        )}
      </>
  );
};

PublishPropertyForm.propTypes = {
  setReloadPosts: PropTypes.func.isRequired,
  setParams: PropTypes.func.isRequired,
};

export default PublishPropertyForm;